import { API_BASE_URL, getBaseHeaders } from './api.config';
import { authenticatedCall } from './auth';
import { throwApiError } from './api.error';
import { PaginatedResponse } from './api.types';

export type ServiceNoticeType = 'TERMS_CHANGE' | 'APP_CLOSURE';
export type ServiceNoticeStatus = 'PENDING' | 'SENDING' | 'ACCEPTED' | 'FAILED' | 'UNCERTAIN' | 'SKIPPED';

export interface ServiceNoticeRequest {
  id: string;
  type: ServiceNoticeType;
  subject: string;
  message: string;
  effectiveAt: string;
  documentId: string | null;
}

export interface ServiceNotice extends ServiceNoticeRequest {
  html: string;
  documentVersion: string | null;
  createdAt: string;
  startedAt: string | null;
  paused: boolean;
  recipientCount: number;
  pending: number;
  sending: number;
  accepted: number;
  failed: number;
  uncertain: number;
  skipped: number;
}

export interface ServiceNoticeIssue {
  id: string;
  email: string;
  status: 'FAILED' | 'UNCERTAIN';
  attemptedAt: string;
}

export interface ServiceNoticeRecipient {
  id: string;
  name: string;
  email: string;
  status: ServiceNoticeStatus;
  attemptedAt: string | null;
  acceptedAt: string | null;
  resolvedAt: string | null;
}

const request = async (path = '', options: RequestInit = {}): Promise<Response> => {
  const response = await authenticatedCall(`${API_BASE_URL}/administration/service-notices${path}`, {
    ...options,
    headers: {
      ...getBaseHeaders(),
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });
  if (!response.ok) {
    await throwApiError(response);
  }
  return response;
};

export const fetchServiceNotices = async (): Promise<ServiceNotice[]> => {
  const response = await request();
  return response.json();
};

export const prepareServiceNotice = async (body: ServiceNoticeRequest): Promise<ServiceNotice> => {
  const response = await request('', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return response.json();
};

export const sendServiceNotice = async (id: string): Promise<ServiceNotice> => {
  const response = await request(`/${id}/send`, { method: 'POST' });
  return response.json();
};

export const pauseServiceNotice = async (id: string): Promise<ServiceNotice> => {
  const response = await request(`/${id}/pause`, { method: 'POST' });
  return response.json();
};

export const retryServiceNotice = async (id: string): Promise<ServiceNotice> => {
  const response = await request(`/${id}/retry`, { method: 'POST' });
  return response.json();
};

export const deleteServiceNoticeDraft = async (id: string): Promise<void> => {
  await request(`/${id}`, { method: 'DELETE' });
};

export const fetchServiceNoticeRecipients = async (
  id: string,
  page = 1,
  status: ServiceNoticeStatus | '' = ''
): Promise<PaginatedResponse<ServiceNoticeRecipient>> => {
  const statusFilter = status ? `&status=${status}` : '';
  const response = await request(`/${id}/recipients?page=${page}&size=20${statusFilter}`);
  return response.json();
};

export const fetchServiceNoticeIssues = async (id: string): Promise<ServiceNoticeIssue[]> => {
  const response = await request(`/${id}/issues`);
  return response.json();
};

export const resolveServiceNoticeIssue = async (
  id: string,
  recipientId: string,
  outcome: 'ACCEPTED' | 'NOT_SENT'
): Promise<ServiceNotice> => {
  const response = await request(`/${id}/recipients/${recipientId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ outcome, providerChecked: true }),
  });
  return response.json();
};
